import type { DashboardDocument, FolderDocument, LabelRow, Pin, SensorBinding, TableConfig, ValueSource } from '@atnt/shared';
import type { SensorDto } from '../api';

/** Every sensor id referenced by a dashboard document (indicator widgets). */
export function dashboardSensorIds(doc: DashboardDocument | null | undefined): string[] {
  if (!doc) return [];
  const ids = new Set<string>();
  for (const el of doc.elements) if (el.type === 'indicator' && el.source.kind === 'sensor') ids.add(el.source.binding.sensorId);
  return [...ids];
}

export const sourceSensorId = (source: ValueSource | null | undefined) => (source?.kind === 'sensor' ? source.binding.sensorId : null);

/** Primary binding + every sensor row of a pin (primary first). */
export function pinSensorIds(pin: Pin): string[] {
  const ids = new Set<string>();
  if (pin.primary) ids.add(pin.primary.sensorId);
  for (const row of pin.rows) {
    const id = sourceSensorId(row.source);
    if (id) ids.add(id);
  }
  return [...ids];
}

export function tableSensorIds(table: TableConfig | null | undefined): string[] {
  if (!table) return [];
  const ids = new Set<string>();
  for (const row of table.rows) if (row.type === 'device') for (const cell of row.cells) if (cell) ids.add(cell.sensorId);
  return [...ids];
}

/** Every sensor id of a folder document: pins, custom table cells, data-layer series. */
export function folderSensorIds(doc: FolderDocument | null | undefined): string[] {
  if (!doc) return [];
  const ids = new Set<string>();
  for (const pin of doc.pins) pinSensorIds(pin).forEach((id) => ids.add(id));
  tableSensorIds(doc.layers.table.custom).forEach((id) => ids.add(id));
  for (const s of doc.layers.data.series) ids.add(s.binding.sensorId);
  return [...ids];
}

/** Title of a pin: explicit title → device code → sensor code. */
export const pinTitle = (pin: Pin) => pin.title || pin.primary?.snapshot.deviceCode || pin.primary?.snapshot.code || '';

/** Unit shown for a binding: override → registry → snapshot. */
export const bindingUnit = (b: SensorBinding, meta?: SensorDto) => b.unitOverride ?? meta?.unit ?? b.snapshot.unit ?? '';

/** Decimals for a binding: override → registry precision → auto. */
export const bindingDecimals = (b: SensorBinding, meta?: SensorDto) => b.decimals ?? meta?.decimals ?? null;

export const rowSensorId = (row: LabelRow) => sourceSensorId(row.source);

/** One (device, sensor) entry per bound sensor of the folder's pins — the "deviceList" table (§6.10). */
export interface DeviceListEntry {
  key: string;
  sensorId: string;
  binding: SensorBinding;
  pinId: string;
  deviceCode: string;
  sensorName: string;
}

export function deviceListEntries(doc: FolderDocument | null | undefined): DeviceListEntry[] {
  if (!doc) return [];
  const seen = new Set<string>();
  const out: DeviceListEntry[] = [];
  const push = (pin: Pin, b: SensorBinding, name?: string) => {
    if (seen.has(b.sensorId)) return;
    seen.add(b.sensorId);
    out.push({
      key: `${pin.id}:${b.sensorId}`,
      sensorId: b.sensorId,
      binding: b,
      pinId: pin.id,
      deviceCode: b.snapshot.deviceCode || pinTitle(pin) || b.snapshot.code,
      sensorName: name || b.snapshot.name,
    });
  };
  for (const pin of doc.pins) {
    if (pin.hidden) continue;
    const rowBindings = pin.rows.flatMap((r) => (r.source.kind === 'sensor' ? [{ b: r.source.binding, name: r.name }] : []));
    if (pin.primary && !rowBindings.some((r) => r.b.sensorId === pin.primary!.sensorId)) push(pin, pin.primary);
    for (const r of rowBindings) push(pin, r.b, r.name);
  }
  // sorted by device code (§6.10); rows of one device keep the pin's order (stable sort)
  return out.sort((a, b) => a.deviceCode.localeCompare(b.deviceCode, undefined, { numeric: true }));
}
